/**
 * 학원 채팅 상담 — 학부모 ↔ 학원 1:1 (카카오톡 스타일)
 * 프로필 헤더 · 말풍선 그룹화 · 카톡식 입력창(첨부: 이미지/달력/출석)
 * [수강 신청] → 자녀 선택 → 신청 (선택 자녀가 학원 학생으로 자동 등록)
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoArrowBack, IoCallOutline, IoAdd, IoMenu, IoSearch, IoClose,
  IoImageOutline, IoCalendarOutline, IoClipboardOutline,
} from 'react-icons/io5';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import academyService from '../../services/academyService';
import { useDialog } from '../../context/DialogContext';

const WEEK = ['일', '월', '화', '수', '목', '금', '토'];

const fmtTime = (ts) => {
  if (!ts?.seconds) return '';
  const d = new Date(ts.seconds * 1000);
  const h = d.getHours();
  const ampm = h < 12 ? '오전' : '오후';
  return `${ampm} ${h % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const fmtDateLine = (ts) => {
  if (!ts?.seconds) return '';
  const d = new Date(ts.seconds * 1000);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 ${WEEK[d.getDay()]}요일`;
};

const dayKey = (ts) => (ts?.seconds ? new Date(ts.seconds * 1000).toDateString() : '');
const minKey = (ts) => {
  if (!ts?.seconds) return '';
  const d = new Date(ts.seconds * 1000);
  return `${d.toDateString()} ${d.getHours()}:${d.getMinutes()}`;
};

// 이미지 압축 (긴 변 1280px, jpeg 0.8)
const compressImage = (file) => new Promise((resolve) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    URL.revokeObjectURL(url);
    const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', 0.8);
  };
  img.onerror = () => resolve(file);
  img.src = url;
});

const AcademyChatPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { myAcademies, reloadAcademies } = useAcademy();
  const { confirm, alert } = useDialog();

  const [academy, setAcademy] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [applying, setApplying] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [uploading, setUploading] = useState(false);
  const endRef = useRef(null);
  const imgInputRef = useRef(null);

  const roomId = userData?.uid || null;
  const isMember = myAcademies.some((a) => a.id === id);
  const children = userData?.children || [];

  const sq = searchQ.trim();
  const shown = sq
    ? messages.filter((m) => m.type !== 'enroll' && (m.text || '').includes(sq))
    : messages;

  useEffect(() => { academyService.getAcademy(id).then(setAcademy); }, [id]);

  useEffect(() => {
    if (!roomId) return undefined;
    return academyService.watchChatMessages(id, roomId, setMessages);
  }, [id, roomId]);

  // 채팅방을 보고 있는 동안 학부모 안읽음 0으로 유지
  useEffect(() => {
    if (roomId) academyService.markChatRead(id, roomId, 'parent');
  }, [id, roomId, messages.length]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages, uploading]);

  const send = async () => {
    const t = text.trim();
    if (!t || !roomId) return;
    setText('');
    try {
      await academyService.sendChatMessage(id, roomId, {
        senderId: roomId, senderName: userData?.name || '', text: t,
      });
    } catch (e) { console.error('메시지 전송 실패:', e); }
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !roomId) return;
    setAttachOpen(false);
    setUploading(true);
    try {
      const blob = await compressImage(file);
      const url = await academyService.uploadChatImage(id, roomId, blob);
      await academyService.sendChatMessage(id, roomId, {
        senderId: roomId, senderName: userData?.name || '', type: 'image', fileUrl: url,
      });
    } catch (err) {
      console.error('이미지 전송 실패:', err);
      alert('이미지 전송에 실패했어요.');
    } finally {
      setUploading(false);
    }
  };

  const comingSoon = (label) => {
    setAttachOpen(false);
    setMenuOpen(false);
    alert(`${label} 기능은 곧 추가할게요!`);
  };

  const onLeave = async () => {
    setMenuOpen(false);
    const ok = await confirm({
      title: '채팅방 나가기',
      message: '채팅방을 나가시겠어요?\n채팅 목록에서 사라져요.',
      confirmText: '나가기',
      danger: true,
    });
    if (!ok) return;
    await academyService.leaveChatRoom(id, roomId);
    navigate('/app/chat');
  };

  const onEnrollClick = async () => {
    if (!roomId) { navigate('/app/login'); return; }
    if (children.length === 0) {
      const go = await confirm({
        title: '자녀 등록 필요',
        message: '수강 신청하려면 자녀를 먼저 등록해야 해요.\n자녀 등록하러 갈까요?',
        confirmText: '등록하러 가기',
      });
      if (go) navigate('/app/profile', { state: { openChild: true } });
      return;
    }
    setPicked(children.length === 1 ? [children[0].id] : []);
    setPickOpen(true);
  };

  const togglePick = (cid) => {
    setPicked((p) => (p.includes(cid) ? p.filter((x) => x !== cid) : [...p, cid]));
  };

  const doEnroll = async () => {
    const selected = children.filter((c) => picked.includes(c.id));
    if (selected.length === 0) return;
    setApplying(true);
    try {
      const names = selected.map((c) => c.name).join(', ');
      await academyService.sendChatMessage(id, roomId, {
        senderId: roomId, senderName: userData?.name || '',
        text: `${userData?.name || '학부모'}님이 ${names} 자녀를 수강 신청했어요.`, type: 'enroll',
      });
      await academyService.joinAcademy(id, roomId, selected);
      await reloadAcademies();
      setPickOpen(false);
    } catch (e) {
      console.error('수강 신청 실패:', e);
      alert('수강 신청에 실패했습니다.');
    } finally {
      setApplying(false);
    }
  };

  if (!roomId) {
    return (
      <Wrap>
        <TopBar>
          <BackBtn onClick={() => navigate(-1)}><IoArrowBack size={22} /></BackBtn>
          <TopTitle>채팅 상담</TopTitle>
        </TopBar>
        <Guide>
          <GuideText>로그인하면 학원과 채팅 상담할 수 있어요.</GuideText>
          <GuideBtn onClick={() => navigate('/app/login')}>로그인</GuideBtn>
        </Guide>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <TopBar>
        <BackBtn onClick={() => navigate(-1)}><IoArrowBack size={22} /></BackBtn>
      </TopBar>

      <ProfileHeader>
        <PhotoBox $url={academy?.photoUrl}>
          {!academy?.photoUrl && (academy?.name || '?').slice(0, 1)}
        </PhotoBox>
        <PInfo>
          <PName>{academy?.name || '채팅 상담'}</PName>
          {academy?.subject && <PSub>{academy.subject}</PSub>}
        </PInfo>
        <HeaderActions>
          <IconBtn
            type="button"
            onClick={() => { setSearchOpen((v) => !v); setSearchQ(''); }}
          >
            <IoSearch size={20} />
          </IconBtn>
          {academy?.phone && (
            <IconBtn
              type="button"
              onClick={() => { window.location.href = `tel:${academy.phone}`; }}
            >
              <IoCallOutline size={20} />
            </IconBtn>
          )}
          <IconBtn type="button" onClick={() => setMenuOpen((v) => !v)}>
            <IoMenu size={22} />
          </IconBtn>
        </HeaderActions>
      </ProfileHeader>

      {searchOpen && (
        <SearchBar>
          <IoSearch size={17} color="#8A8F9C" />
          <SearchInput
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="대화 내용 검색"
            autoFocus
          />
          <SearchClose
            type="button"
            onClick={() => { setSearchOpen(false); setSearchQ(''); }}
          >
            <IoClose size={18} />
          </SearchClose>
        </SearchBar>
      )}

      {menuOpen && (
        <MenuOverlay onClick={() => setMenuOpen(false)}>
          <Menu onClick={(e) => e.stopPropagation()}>
            <MenuItem type="button" onClick={() => comingSoon('게시판')}>게시판</MenuItem>
            <MenuDivider />
            <MenuItem type="button" $danger onClick={onLeave}>채팅방 나가기</MenuItem>
          </Menu>
        </MenuOverlay>
      )}

      {!isMember && (
        <EnrollBar>
          <EnrollText>상담 후 마음에 들면 바로 신청하세요</EnrollText>
          <EnrollBtn type="button" disabled={applying} onClick={onEnrollClick}>
            수강 신청
          </EnrollBtn>
        </EnrollBar>
      )}

      <Msgs onClick={() => setAttachOpen(false)}>
        {shown.length === 0 && (
          <Empty>{sq ? '검색 결과가 없어요.' : '학원에 궁금한 점을 물어보세요.'}</Empty>
        )}
        {shown.map((m, i) => {
          const prev = shown[i - 1];
          const next = shown[i + 1];
          const showDate = !prev || dayKey(prev.createdAt) !== dayKey(m.createdAt);

          if (m.type === 'enroll') {
            return (
              <React.Fragment key={m.id}>
                {showDate && <DateLine><span>{fmtDateLine(m.createdAt)}</span></DateLine>}
                <SysMsg>{m.text || '수강 신청했어요'}</SysMsg>
              </React.Fragment>
            );
          }

          const mine = m.senderId === roomId;
          const grpPrev = !showDate && prev && prev.type !== 'enroll'
            && prev.senderId === m.senderId && minKey(prev.createdAt) === minKey(m.createdAt);
          const grpNext = next && next.type !== 'enroll'
            && next.senderId === m.senderId && minKey(next.createdAt) === minKey(m.createdAt)
            && dayKey(next.createdAt) === dayKey(m.createdAt);

          return (
            <React.Fragment key={m.id}>
              {showDate && <DateLine><span>{fmtDateLine(m.createdAt)}</span></DateLine>}
              <MsgRow $mine={mine} $grp={grpPrev}>
                {!mine && (
                  <AvatarSlot>
                    {!grpPrev && (
                      <Avatar $url={academy?.photoUrl}>
                        {!academy?.photoUrl && (academy?.name || '?').slice(0, 1)}
                      </Avatar>
                    )}
                  </AvatarSlot>
                )}
                <Col $mine={mine}>
                  {!mine && !grpPrev && <SenderName>{academy?.name || '학원'}</SenderName>}
                  <BubbleLine $mine={mine}>
                    {m.type === 'image' ? (
                      <ImageMsg
                        type="button"
                        onClick={() => window.open(m.fileUrl, '_blank')}
                      >
                        <img src={m.fileUrl} alt="첨부 이미지" />
                      </ImageMsg>
                    ) : (
                      <Bubble $mine={mine}>{m.text}</Bubble>
                    )}
                    {!grpNext && <TimeText>{fmtTime(m.createdAt)}</TimeText>}
                  </BubbleLine>
                </Col>
              </MsgRow>
            </React.Fragment>
          );
        })}
        {uploading && (
          <MsgRow $mine>
            <Col $mine>
              <BubbleLine $mine>
                <Bubble $mine>사진 전송 중…</Bubble>
              </BubbleLine>
            </Col>
          </MsgRow>
        )}
        <div ref={endRef} />
      </Msgs>

      <InputArea>
        {attachOpen && (
          <AttachGrid>
            <AttachBtn type="button" onClick={() => imgInputRef.current?.click()}>
              <AttachIco><IoImageOutline size={23} /></AttachIco>
              이미지
            </AttachBtn>
            <AttachBtn type="button" onClick={() => comingSoon('달력')}>
              <AttachIco><IoCalendarOutline size={23} /></AttachIco>
              달력
            </AttachBtn>
            <AttachBtn type="button" onClick={() => comingSoon('출석 정보')}>
              <AttachIco><IoClipboardOutline size={23} /></AttachIco>
              출석 정보
            </AttachBtn>
          </AttachGrid>
        )}
        <ChatInput
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setAttachOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder="메시지 입력"
          rows={2}
        />
        <ActionRow>
          <PlusBtn type="button" $on={attachOpen} onClick={() => setAttachOpen((v) => !v)}>
            <IoAdd size={24} />
          </PlusBtn>
          <SendTextBtn
            type="button"
            $on={!!text.trim() && !uploading}
            disabled={!text.trim() || uploading}
            onClick={send}
          >
            전송
          </SendTextBtn>
        </ActionRow>
        <input ref={imgInputRef} type="file" accept="image/*" hidden onChange={onPickImage} />
      </InputArea>

      {pickOpen && (
        <Overlay onClick={() => setPickOpen(false)}>
          <Sheet onClick={(e) => e.stopPropagation()}>
            <SheetTitle>수강 신청할 자녀</SheetTitle>
            <SheetSub>이 학원에 다닐 자녀를 선택해 주세요.</SheetSub>
            {children.map((c) => (
              <PickRow key={c.id} type="button" $on={picked.includes(c.id)} onClick={() => togglePick(c.id)}>
                <Check $on={picked.includes(c.id)} />
                <PickName>{c.name}</PickName>
              </PickRow>
            ))}
            <ConfirmBtn type="button" disabled={picked.length === 0 || applying} onClick={doEnroll}>
              {applying ? '신청 중...' : `수강 신청 (${picked.length}명)`}
            </ConfirmBtn>
          </Sheet>
        </Overlay>
      )}
    </Wrap>
  );
};

export default AcademyChatPage;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #fff;
`;

const TopBar = styled.div`
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const BackBtn = styled.button`
  background: none;
  display: flex;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TopTitle = styled.p`
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ProfileHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 16px 16px;
`;

const PhotoBox = styled.div`
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  font-weight: 800;
  color: #8A8F9C;
  background: ${({ $url }) =>
    $url ? `#EDEFF3 url(${$url}) center/cover` : '#E7E8EC'};
`;

const PInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PName = styled.p`
  font-size: 17px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PSub = styled.p`
  margin-top: 3px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const HeaderActions = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 2px;
`;

const IconBtn = styled.button`
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SearchBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 16px 12px;
  padding: 9px 12px;
  border-radius: 12px;
  background: #F2F3F7;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  &:focus { outline: none; }
  &::placeholder { color: ${({ theme }) => theme.colors.text.hint}; }
`;

const SearchClose = styled.button`
  flex-shrink: 0;
  display: flex;
  background: none;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const MenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 150;
`;

const Menu = styled.div`
  position: absolute;
  top: 60px;
  right: 12px;
  min-width: 168px;
  background: #fff;
  border-radius: 14px;
  padding: 6px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.16);
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 12px 12px;
  text-align: left;
  background: none;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $danger, theme }) => ($danger ? theme.colors.accent.red : theme.colors.text.primary)};
  &:active { background: #F4F5F7; }
`;

const MenuDivider = styled.div`
  height: 1px;
  margin: 4px 8px;
  background: ${({ theme }) => theme.colors.divider};
`;

const EnrollBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const EnrollText = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

const EnrollBtn = styled.button`
  flex-shrink: 0;
  padding: 8px 16px;
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  &:disabled { opacity: 0.5; }
`;

const Msgs = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 18px;
  display: flex;
  flex-direction: column;
  background: #EDEEF2;
`;

const Empty = styled.p`
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 40px 0;
`;

const DateLine = styled.div`
  display: flex;
  justify-content: center;
  margin: 14px 0;
  span {
    padding: 4px 12px;
    border-radius: 11px;
    font-size: 11.5px;
    font-weight: 600;
    color: #fff;
    background: rgba(0, 0, 0, 0.22);
  }
`;

const SysMsg = styled.div`
  align-self: center;
  margin: 6px 0;
  padding: 7px 14px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: rgba(0, 0, 0, 0.055);
`;

const MsgRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-top: ${({ $grp }) => ($grp ? '2px' : '11px')};
  justify-content: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
`;

const AvatarSlot = styled.div`
  width: 36px;
  flex-shrink: 0;
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  color: #8A8F9C;
  background: ${({ $url }) =>
    $url ? `#EDEFF3 url(${$url}) center/cover` : '#E7E8EC'};
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 74%;
  align-items: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
`;

const SenderName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 4px;
  padding-left: 2px;
`;

const BubbleLine = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5px;
  flex-direction: ${({ $mine }) => ($mine ? 'row-reverse' : 'row')};
`;

const Bubble = styled.div`
  padding: 9px 12px;
  border-radius: 15px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ $mine }) => ($mine ? '#fff' : '#1F2330')};
  background: ${({ $mine, theme }) => ($mine ? theme.colors.primary : '#fff')};
`;

const ImageMsg = styled.button`
  max-width: 210px;
  border-radius: 15px;
  overflow: hidden;
  background: #fff;
  line-height: 0;
  img { width: 100%; display: block; }
`;

const TimeText = styled.span`
  flex-shrink: 0;
  font-size: 10.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-bottom: 1px;
`;

const InputArea = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 8px 12px 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

const AttachGrid = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 4px 14px;
`;

const AttachBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  background: none;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const AttachIco = styled.span`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5B616E;
  background: #F2F3F7;
`;

const ChatInput = styled.textarea`
  width: 100%;
  padding: 8px 6px;
  border: none;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.45;
  resize: none;
  height: 52px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  &:focus { outline: none; }
  &::placeholder { color: ${({ theme }) => theme.colors.text.hint}; }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const PlusBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  color: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.text.secondary)};
  transition: transform 0.18s;
  transform: rotate(${({ $on }) => ($on ? '45deg' : '0deg')});
`;

const SendTextBtn = styled.button`
  padding: 7px 16px;
  border-radius: 16px;
  font-size: 13.5px;
  font-weight: 700;
  color: #fff;
  background: ${({ $on, theme }) => ($on ? theme.colors.primary : '#C2C5CE')};
  transition: background 0.15s;
`;

const Guide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
`;

const GuideText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const GuideBtn = styled.button`
  padding: 12px 30px;
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 22px 18px 28px;
`;

const SheetTitle = styled.p`
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SheetSub = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 6px 0 16px;
`;

const PickRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 14px;
  margin-bottom: 7px;
  border-radius: 12px;
  background: ${({ $on, theme }) => ($on ? theme.colors.primaryLight : '#F4F5F7')};
  text-align: left;
`;

const Check = styled.span`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 6px;
  background: ${({ $on, theme }) => ($on ? theme.colors.primary : '#fff')};
  border: 2px solid ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.border)};
  position: relative;
  &::after {
    content: ${({ $on }) => ($on ? "'✓'" : "''")};
    position: absolute;
    inset: 0;
    color: #fff;
    font-size: 12px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const PickName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ConfirmBtn = styled.button`
  width: 100%;
  margin-top: 14px;
  padding: 15px;
  border-radius: 13px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  &:disabled { background: #D7D9E0; }
`;
